import { ExternalLink } from "lucide-react";
import { accorder } from "./LignesImportEvalNat";

/**
 * Les questions au délégué avant l'import des résultats nationaux (plan du 30/10/2026, étape 3) :
 *   - « Même personne ? » : un élève que le serveur ne range pas seul, nom écrit autrement ou fiche dans une autre
 *     classe (« quand il y a un doute, c'est le prof qui confirme », 31/10/2026). Le nom et la classe du fichier du
 *     ministère, puis chaque fiche repérée dans TanoEd avec sa classe, et la question ;
 *   - « Laquelle ? » : plusieurs fiches du niveau portent le nom, le délégué désigne la bonne.
 * Une question par élève pour ses deux matières. Rien n'est coché d'avance et aucune réponse ne bloque l'import :
 * sans « Oui », les résultats de l'élève ne sont pas importés. Après « Non » ou « Aucune », la question dit quoi faire
 * d'un nouvel élève, avec les mots de l'encadré des inconnus : l'import des élèves, puis « Revérifier » (01/11/2026).
 * `choix` : clé de question → numéro de fiche, ou null pour « Non » et « Aucune ».
 */

const BOUTON = "px-3 py-1.5 rounded-lg text-xs font-bold transition-colors";

const fiche = (f) => `${f.nom} ${f.prenom} (${f.classe_nom})`;

function Question({ question, reponse, onChoisir, onAnnuler, onOuvrirImportEleves, onReverifier }) {
    const { cle, type, nom, prenom, classe } = question;
    const laquelle = type === "lequel";
    const fiches = laquelle ? question.candidats : question.propositions;
    const choisie = fiches.find((f) => f.eleve_id === reponse);
    const seule = !laquelle && fiches.length === 1;
    const non = (
        <button type="button" onClick={() => onChoisir(cle, null)} className={`${BOUTON} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
            {laquelle ? "Aucune" : seule ? "Non, autre personne" : "Non, aucune de ces personnes"}
        </button>
    );

    return (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex flex-col gap-2">
            {laquelle ? (
                <p className="text-sm text-slate-800">
                    Le fichier de la {classe.nom} écrit <b className="font-mono text-[13px]">{nom} {prenom}</b>. Plusieurs fiches de TanoEd portent ce nom : laquelle ?
                </p>
            ) : (
                <p className="text-sm text-slate-800">
                    Dans le fichier du ministère : <b className="font-mono text-[13px]">{nom} {prenom}</b>, en {classe.nom}
                </p>
            )}
            {reponse !== undefined ? (
                <div className="flex flex-col gap-1.5 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={choisie ? "font-semibold text-emerald-700" : "font-semibold text-slate-600"}>
                            {choisie
                                ? `Ses résultats iront sur la fiche ${laquelle ? `de la ${choisie.classe_nom}` : `de ${fiche(choisie)}`}.`
                                : "Ses résultats ne seront pas importés."}
                        </span>
                        <button type="button" onClick={() => onAnnuler(cle)} className={`${BOUTON} bg-white border border-slate-300 text-slate-600 hover:bg-slate-50`}>
                            Annuler
                        </button>
                    </div>
                    {choisie ? null : (
                        <>
                            <p className="text-slate-700">
                                <span className="font-semibold">Pas encore dans TanoEd ?</span> Passez par l'import des élèves, puis revérifiez.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={onOuvrirImportEleves} className={`${BOUTON} inline-flex items-center gap-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
                                    Ouvrir l'import des élèves
                                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                                </button>
                                <button type="button" onClick={onReverifier} className={`${BOUTON} bg-white border border-slate-300 text-slate-700 hover:bg-slate-50`}>
                                    Revérifier
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : laquelle ? (
                <div className="flex flex-col gap-1.5">
                    {fiches.map((f) => (
                        <div key={f.eleve_id} className="flex flex-wrap items-center gap-2 text-sm">
                            <button type="button" onClick={() => onChoisir(cle, f.eleve_id)} className={`${BOUTON} bg-emerald-600 text-white hover:bg-emerald-700`}>
                                La fiche de la {f.classe_nom}
                            </button>
                        </div>
                    ))}
                    <div>{non}</div>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {fiches.map((f) => (
                        <div key={f.eleve_id} className="flex flex-col gap-1 text-sm text-slate-800">
                            <span>
                                Dans TanoEd : <b className="font-mono text-[13px]">{f.nom} {f.prenom}</b>, en {f.classe_nom}
                            </span>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold">Même personne ?</span>
                                <button
                                    type="button"
                                    onClick={() => onChoisir(cle, f.eleve_id)}
                                    aria-label={`Oui, même personne que ${fiche(f)}`}
                                    className={`${BOUTON} bg-emerald-600 text-white hover:bg-emerald-700`}
                                >
                                    Oui, même personne
                                </button>
                                {seule ? non : null}
                            </div>
                        </div>
                    ))}
                    {seule ? null : <div>{non}</div>}
                </div>
            )}
        </div>
    );
}

/** `onOuvrirImportEleves`, `onReverifier` : les deux boutons qui suivent un « Non » ou un « Aucune ». */
function NomsAConfirmerEvalNat({ questions, choix, onChoisir, onAnnuler, onOuvrirImportEleves, onReverifier }) {
    if (questions.length === 0) return null;
    return (
        <div role="region" aria-label="Élèves à confirmer" className="flex flex-col gap-2">
            <div>
                <h3 className="text-sm font-bold text-slate-800">{accorder(questions.length, "élève à confirmer", "élèves à confirmer")}</h3>
                <p className="text-xs text-slate-500">
                    Répondez pour chaque élève. Sans réponse, ses résultats ne sont pas importés ; ceux des autres le sont quand même.
                </p>
            </div>
            {questions.map((question) => (
                <Question
                    key={question.cle}
                    question={question}
                    reponse={choix[question.cle]}
                    onChoisir={onChoisir}
                    onAnnuler={onAnnuler}
                    onOuvrirImportEleves={onOuvrirImportEleves}
                    onReverifier={onReverifier}
                />
            ))}
        </div>
    );
}

export default NomsAConfirmerEvalNat;
